import { useMemo, useState } from 'react';
import { Filter, Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { BosDurum, Buton, OnayDialog, Rozet, VeriTablo, toast, type ColumnDef } from '@emanetx/ui';
import type { FiyatKurali } from '@emanetx/sdk';
import { useFiyatKurallari, useFiyatKuralGuncelle, useFiyatKuralSil } from './api.js';
import { FiyatKuralDialog, gunlerOzeti } from './fiyat-kural-dialog.js';

const BOYUT_ETIKET: Record<string, string> = {
  KUCUK: 'S',
  ORTA: 'M',
  BUYUK: 'L',
  EKSTRA: 'XL',
};

export function SayfaFiyatKurallari() {
  const [sayfa, setSayfa] = useState(1);
  const [aktif, setAktif] = useState<'' | 'evet' | 'hayir'>('');
  const [dialogAcik, setDialogAcik] = useState(false);
  const [duzenleHedef, setDuzenleHedef] = useState<FiyatKurali | null>(null);
  const [silHedef, setSilHedef] = useState<FiyatKurali | null>(null);

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(aktif === 'evet' && { aktif: true }),
    ...(aktif === 'hayir' && { aktif: false }),
  };

  const liste = useFiyatKurallari(sorgu);
  const guncelle = useFiyatKuralGuncelle();
  const sil = useFiyatKuralSil();

  const aktifToggle = async (k: FiyatKurali) => {
    try {
      await guncelle.mutateAsync({ id: k.id, aktif: !k.aktif });
      toast.success(k.aktif ? 'Pasifleştirildi' : 'Aktifleştirildi');
    } catch {
      toast.error('Toggle başarısız');
    }
  };

  const kolonlar = useMemo<ColumnDef<FiyatKurali, unknown>[]>(
    () => [
      {
        accessorKey: 'aktif',
        header: 'Aktif',
        meta: { etiket: 'Aktif' },
        cell: ({ row }) => (
          <label
            className="inline-flex cursor-pointer items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={row.original.aktif}
              onChange={() => void aktifToggle(row.original)}
              className="accent-marka-500 h-4 w-4 cursor-pointer"
            />
          </label>
        ),
      },
      {
        accessorKey: 'oncelik',
        header: 'Öncelik',
        meta: { etiket: 'Öncelik' },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.oncelik}</span>,
      },
      {
        accessorKey: 'noktaKod',
        header: 'Nokta',
        meta: { etiket: 'Nokta' },
        cell: ({ row }) =>
          row.original.noktaKod ? (
            <span className="text-marka-700 dark:text-marka-400 font-mono text-xs">
              {row.original.noktaKod}
            </span>
          ) : (
            <Rozet ton="fume">Tümü</Rozet>
          ),
      },
      {
        accessorKey: 'dolapBoyutu',
        header: 'Boyut',
        meta: { etiket: 'Boyut' },
        cell: ({ row }) =>
          row.original.dolapBoyutu ? (
            <Rozet ton="fume">{BOYUT_ETIKET[row.original.dolapBoyutu]}</Rozet>
          ) : (
            <span className="text-fume-400 text-xs">—</span>
          ),
      },
      {
        id: 'gunler',
        header: 'Günler',
        meta: { etiket: 'Günler' },
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-fume-600 dark:text-fume-300 text-xs">
            {gunlerOzeti(row.original.gunlerMask)}
          </span>
        ),
      },
      {
        id: 'saat',
        header: 'Saat',
        meta: { etiket: 'Saat' },
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {String(row.original.saatBaslangic).padStart(2, '0')}:00–
            {String(row.original.saatBitis).padStart(2, '0')}:00
          </span>
        ),
      },
      {
        id: 'etki',
        header: 'Etki',
        meta: { etiket: 'Etki' },
        enableSorting: false,
        cell: ({ row }) =>
          row.original.sabitTutar ? (
            <span className="font-mono text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              ={row.original.sabitTutar}
            </span>
          ) : (
            <span className="font-mono text-xs font-semibold">
              ×{Number(row.original.carpan).toFixed(2)}
            </span>
          ),
      },
      {
        accessorKey: 'aciklama',
        header: 'Açıklama',
        meta: { etiket: 'Açıklama' },
        cell: ({ row }) => (
          <span className="text-fume-700 dark:text-fume-200 block max-w-xs truncate text-xs">
            {row.original.aciklama}
          </span>
        ),
      },
      {
        id: 'aksiyon',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Düzenle"
              onClick={() => {
                setDuzenleHedef(row.original);
                setDialogAcik(true);
              }}
            >
              <Pencil size={14} />
            </Buton>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Sil"
              onClick={() => setSilHedef(row.original)}
            >
              <Trash2 size={14} className="text-red-500" />
            </Buton>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Fiyat Kuralları</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Saatlik fiyat için çarpan veya sabit tutar kuralları. Öncelik düşük olan önce uygulanır.
          </p>
        </div>
        <Buton
          onClick={() => {
            setDuzenleHedef(null);
            setDialogAcik(true);
          }}
        >
          <Plus size={16} /> Yeni Kural
        </Buton>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={Tag}
            baslik="Henüz fiyat kuralı yok"
            aciklama="İlk kuralı eklemek için 'Yeni Kural' butonunu kullan."
          />
        }
        ustAksiyonlar={
          <>
            <Filter size={13} className="text-fume-400" />
            <select
              value={aktif}
              onChange={(e) => {
                setAktif(e.target.value as '' | 'evet' | 'hayir');
                setSayfa(1);
              }}
              className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs"
            >
              <option value="">Tüm kurallar</option>
              <option value="evet">Sadece aktif</option>
              <option value="hayir">Sadece pasif</option>
            </select>
          </>
        }
      />

      <FiyatKuralDialog
        acik={dialogAcik}
        onDegisim={(o) => {
          setDialogAcik(o);
          if (!o) setDuzenleHedef(null);
        }}
        kural={duzenleHedef}
      />

      <OnayDialog
        acik={!!silHedef}
        onDegisim={(o) => !o && setSilHedef(null)}
        baslik="Kuralı sil?"
        aciklama={silHedef?.aciklama ?? ''}
        onayMetni="Sil"
        tur="tehlike"
        onOnay={async () => {
          if (!silHedef) return;
          try {
            await sil.mutateAsync(silHedef.id);
            toast.success('Kural silindi');
            setSilHedef(null);
          } catch {
            toast.error('Silinemedi');
          }
        }}
      />
    </div>
  );
}
